import axios from 'axios';
import api from './api';

export interface AdminSetting {
  id: number;
  key: string;
  value: string;
  description: string | null;
  category: string;
  updatedBy: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyInfo {
  id: number;
  name: string;
  nameEn: string | null;
  postalCode: string | null;
  prefecture: string | null;
  city: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  invoiceRegNumber: string | null;
  logoUrl: string | null;
  sealUrl: string | null;
  description: string | null;
  descriptionEn: string | null;
  updatedBy: number | null;
  createdAt: string;
  updatedAt: string;
  updater?: {
    id: number;
    username: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
}

export interface EmailTemplate {
  id: number;
  templateKey: string;
  name: string;
  description: string | null;
  subjectJa: string;
  bodyJa: string;
  subjectEn: string | null;
  bodyEn: string | null;
  subjectZhCn: string | null;
  bodyZhCn: string | null;
  subjectFr: string | null;
  bodyFr: string | null;
  subjectEs: string | null;
  bodyEs: string | null;
  subjectDe: string | null;
  bodyDe: string | null;
  subjectPt: string | null;
  bodyPt: string | null;
  subjectRu: string | null;
  bodyRu: string | null;
  variables: any;
  category: string;
  isActive: boolean;
  updatedBy: number | null;
  createdAt: string;
  updatedAt: string;
  updater?: {
    id: number;
    username: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
}

export interface EmailHistory {
  id: number;
  templateId: number | null;
  templateKey: string | null;
  to: string;
  cc: string | null;
  bcc: string | null;
  subject: string;
  body: string;
  language: string;
  status: string;
  errorMessage: string | null;
  variables: any;
  sentBy: number | null;
  sentAt: string;
  createdAt: string;
  template?: {
    id: number;
    templateKey: string;
    name: string;
  } | null;
  sender?: {
    id: number;
    username: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
}

export const adminService = {
  // 管理者設定
  async getSettings(): Promise<AdminSetting[]> {
    const response = await api.get('/admin/settings');
    return response.data.data;
  },

  async getSetting(key: string): Promise<AdminSetting> {
    const response = await api.get(`/admin/settings/${key}`);
    return response.data.data;
  },

  async updateSetting(
    key: string,
    value: string,
    description: string | null,
    category: string
  ): Promise<AdminSetting> {
    const response = await api.put(`/admin/settings/${key}`, { value, description, category });
    return response.data.data;
  },

  async deleteSetting(key: string): Promise<void> {
    await api.delete(`/admin/settings/${key}`);
  },

  // 会社情報
  async getCompanyInfo(): Promise<CompanyInfo | null> {
    const response = await api.get('/admin/company-info');
    return response.data.data;
  },

  async updateCompanyInfo(data: Partial<CompanyInfo>): Promise<CompanyInfo> {
    const response = await api.put('/admin/company-info', data);
    return response.data.data;
  },

  // メールテンプレート
  async getEmailTemplates(options?: {
    category?: string;
    isActive?: boolean;
  }): Promise<EmailTemplate[]> {
    const params = new URLSearchParams();
    if (options?.category) params.append('category', options.category);
    if (options?.isActive !== undefined) params.append('isActive', String(options.isActive));
    const response = await api.get(`/admin/email-templates?${params.toString()}`);
    return response.data.data;
  },

  async getEmailTemplate(id: number): Promise<EmailTemplate> {
    const response = await api.get(`/admin/email-templates/${id}`);
    return response.data.data;
  },

  async createEmailTemplate(data: Partial<EmailTemplate>): Promise<EmailTemplate> {
    const response = await api.post('/admin/email-templates', data);
    return response.data.data;
  },

  async updateEmailTemplate(id: number, data: Partial<EmailTemplate>): Promise<EmailTemplate> {
    const response = await api.put(`/admin/email-templates/${id}`, data);
    return response.data.data;
  },

  async deleteEmailTemplate(id: number): Promise<void> {
    await api.delete(`/admin/email-templates/${id}`);
  },

  // メール送信履歴
  async getEmailHistory(options?: {
    page?: number;
    limit?: number;
    templateKey?: string;
    to?: string;
    status?: string;
    language?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{ data: EmailHistory[]; pagination: any }> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', String(options.page));
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.templateKey) params.append('templateKey', options.templateKey);
    if (options?.to) params.append('to', options.to);
    if (options?.status) params.append('status', options.status);
    if (options?.language) params.append('language', options.language);
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    const response = await api.get(`/admin/email-history?${params.toString()}`);
    return response.data;
  },

  async getEmailHistoryById(id: number): Promise<EmailHistory> {
    const response = await api.get(`/admin/email-history/${id}`);
    return response.data.data;
  },

  async sendEmail(data: {
    templateId?: number;
    templateKey?: string;
    to: string;
    cc?: string | null;
    bcc?: string | null;
    subject: string;
    body: string;
    language?: string;
    variables?: any;
  }): Promise<EmailHistory> {
    const response = await api.post('/admin/email-history/send', data);
    return response.data.data;
  },

  // システム統計
  async getSystemStats(): Promise<any> {
    const response = await api.get('/admin/system-stats');
    return response.data.data;
  },

  // 設定のエクスポート
  async exportSettings(): Promise<any> {
    const response = await api.get('/admin/export-settings');
    return response.data.data;
  },

  // 設定のインポート
  async importSettings(data: any): Promise<any> {
    const response = await api.post('/admin/import-settings', data);
    return response.data.data;
  },

  // ユーザー行動ログ一覧（管理者用）
  async listUserActivity(params: {
    page?: number;
    limit?: number;
    userId?: number;
    featureKey?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
  }): Promise<{
    success: boolean;
    data: {
      logs: UserActivityLogEntry[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    };
  }> {
    const response = await api.get('/admin/user-activity', { params });
    return response.data;
  },

  // 機能別利用数（管理者用・グラフ用）
  async getFeatureUsage(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<{
    success: boolean;
    data: Array<{ featureKey: string; count: number; label: string }>;
  }> {
    const response = await api.get('/admin/user-activity/feature-usage', { params });
    return response.data;
  },

  // ユーザー行動ログをCSVエクスポート（現在のフィルター条件を適用）
  async exportUserActivity(params: {
    userId?: number;
    featureKey?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
  }): Promise<Blob> {
    const response = await api.get('/admin/user-activity/export', {
      params,
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data as Blob;
  },

  async getHostSecurity(refresh = false): Promise<{ success: boolean; data: HostSecuritySummary }> {
    const response = await api.get('/admin/host-security', {
      params: refresh ? { refresh: '1' } : undefined,
      timeout: 120000,
    });
    return response.data as { success: boolean; data: HostSecuritySummary };
  },
};

export interface HostSecurityCount {
  key: string;
  count: number;
}

export interface HostSecuritySummary {
  hostname: string;
  generatedAt: string;
  windows: {
    auth: { from: string; to: string } | null;
    mail: { from: string; to: string } | null;
    web: { from: string; to: string } | null;
  };
  ssh: {
    invalidUserEvents: number;
    failedPasswordEvents: number;
    preauthDisconnects: number;
    uniqueFailIps: number;
    rootAccepted: number;
    passwordAccepted: number;
    accepted: { user: string; method: string; count: number; addresses: string[] }[];
    topUsers: HostSecurityCount[];
    topIps: HostSecurityCount[];
  };
  mail: {
    authFailures: number;
    invalidMechanism: number;
    connectionLost: number;
    otherFailures: number;
    topUsers: HostSecurityCount[];
    successes: { user: string; address: string; count: number }[];
  };
  web: {
    probeRequests: number;
    uniqueIps: number;
    byStatus: HostSecurityCount[];
    appShellResponses: number;
    topPaths: HostSecurityCount[];
  };
  fail2ban: {
    bans: number;
    uniqueIps: number;
    byJail: HostSecurityCount[];
    topIps: HostSecurityCount[];
  };
  checks: { id: string; ok: boolean | null }[];
  unavailable: string[];
}

export interface UserActivityLogEntry {
  id: string;
  userId: number;
  featureKey: string;
  path: string;
  method: string;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  user: {
    id: number;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

